import React, { useEffect, useState } from "react";
import { ShieldCheck, X, ChevronDown, ChevronUp } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { loadConsent, saveConsent, updateConsent } from "../lib/consent";
import { pathFor } from "../lib/paths";
import { Link } from "react-router-dom";

const DEFAULT_STATE = {
  analytics: false,
  marketing: false,
  functional: false,
};

const CATEGORIES = [
  { key: "necessary", locked: true },
  { key: "functional", locked: false, stateKey: "functional" },
  { key: "analytics", locked: false, stateKey: "analytics" },
  { key: "marketing", locked: false, stateKey: "marketing" },
];

function Toggle({ checked, disabled, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors ${
        disabled
          ? "cursor-not-allowed border-slate-300 bg-slate-300"
          : checked
          ? "border-slate-900 bg-slate-900 hover:bg-slate-800"
          : "border-slate-300 bg-white hover:border-slate-500"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow ${
          disabled ? "translate-x-6" : checked ? "translate-x-6" : "translate-x-1"
        }`}
        style={{ transition: "transform 0.15s ease" }}
      />
    </button>
  );
}

export default function CookieBanner() {
  const { lang, t, raw } = useI18n();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [state, setState] = useState(DEFAULT_STATE);

  useEffect(() => {
    const existing = loadConsent();
    if (!existing) {
      setVisible(true);
    } else {
      updateConsent(existing);
    }
    const handler = () => {
      setState(loadConsent() || DEFAULT_STATE);
      setVisible(true);
      setShowDetails(true);
    };
    window.addEventListener("dervaniq:open-cookie-preferences", handler);
    return () => window.removeEventListener("dervaniq:open-cookie-preferences", handler);
  }, []);

  const commit = (next) => {
    setState(next);
    saveConsent(next);
    updateConsent(next);
    setVisible(false);
    setShowDetails(false);
  };

  const acceptAll = () =>
    commit({ analytics: true, marketing: true, functional: true });
  const rejectAll = () =>
    commit({ analytics: false, marketing: false, functional: false });
  const saveChoices = () => commit(state);

  if (!visible) return null;

  return (
    <>
      {/* Subtle backdrop to focus attention without darkening the page */}
      <div
        aria-hidden="true"
        className="fixed inset-0 z-40 bg-slate-950/20"
        style={{ backdropFilter: "blur(2px)" }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-banner-title"
        data-testid="cookie-banner"
        className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-2xl sm:inset-x-6 sm:bottom-6"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div className="relative px-6 pb-6 pt-7 sm:px-8 sm:pb-8 sm:pt-8">
          {/* Header */}
          <div className="flex items-start gap-4">
            <div
              aria-hidden="true"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white"
            >
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="flex-1 pr-8">
              <h2
                id="cookie-banner-title"
                className="text-lg font-bold text-slate-900 sm:text-xl"
                style={{
                  fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif",
                }}
              >
                {t("cookies.title")}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                {t("cookies.description")}{" "}
                <Link
                  to={pathFor("cookies", lang)}
                  data-testid="cookie-banner-learn-more"
                  className="font-semibold text-slate-900 underline decoration-slate-400 underline-offset-2 hover:decoration-slate-900"
                >
                  {t("cookies.learn_more")}
                </Link>
              </p>
            </div>
            <button
              type="button"
              onClick={rejectAll}
              data-testid="cookie-banner-close"
              aria-label="Close"
              className="absolute right-4 top-4 rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Details */}
          {showDetails && (
            <div
              data-testid="cookie-details"
              className="mt-6 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-slate-50"
            >
              {CATEGORIES.map((cat) => (
                <div
                  key={cat.key}
                  data-testid={`cookie-category-${cat.key}`}
                  className="flex items-start justify-between gap-4 p-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">
                        {raw(`cookies.categories.${cat.key}.title`)}
                      </span>
                      {cat.locked && (
                        <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-slate-700">
                          {lang === "fr" ? "Obligatoire" : "Required"}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-slate-600">
                      {raw(`cookies.categories.${cat.key}.description`)}
                    </p>
                  </div>
                  <Toggle
                    checked={cat.locked ? true : state[cat.stateKey]}
                    disabled={cat.locked}
                    onChange={(v) =>
                      setState((s) => ({ ...s, [cat.stateKey]: v }))
                    }
                    testId={`cookie-toggle-${cat.key}`}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              data-testid="cookie-customize"
              className="inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600 hover:text-slate-900"
            >
              {t("cookies.customize")}
              {showDetails ? (
                <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              )}
            </button>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-reject-all"
                className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:border-slate-900 hover:text-slate-900"
              >
                {t("cookies.reject_all")}
              </button>
              {showDetails ? (
                <button
                  type="button"
                  onClick={saveChoices}
                  data-testid="cookie-save"
                  className="inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  {t("cookies.save")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={acceptAll}
                  data-testid="cookie-accept-all"
                  className="inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  {t("cookies.accept_all")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
